import {
  CmpInvoiceDetailFieldsFragment,
  CmpInvoiceDisplayStateEnum,
  CmpInvoiceKindEnum,
  CmpInvoiceLookupOptionFieldsFragment,
  CmpInvoiceOverdueBucketEnum,
  CmpInvoicePaymentChoiceFieldsFragment,
  CmpInvoicePaymentRuleKindEnum,
  CmpInvoiceRecordFieldsFragment,
  CmpInvoiceStatusEnum,
  CmpManageInvoiceRowFieldsFragment,
  CmpOperationalEventTypeEnum,
  CmpOperationalSourceKindEnum,
  CmpPaymentChoiceTypeEnum,
} from "../../../graphql/types";
import {
  MockInvoice,
  MockInvoiceHistoryEvent,
  MockInvoiceLineItem,
  MockInvoiceSourceKind,
  MockInvoiceStatus,
  MockInvoiceType,
} from "./mockInvoiceData";

const getPaymentMethodFallbackLabel = (status: CmpInvoiceStatusEnum) => {
  if (status === CmpInvoiceStatusEnum.Waived) {
    return "Waived";
  }

  if (status === CmpInvoiceStatusEnum.Paid || status === CmpInvoiceStatusEnum.Refunded) {
    return "-";
  }

  return "Not paid";
};

export const dateInputToUtcMilli = (value: string) => {
  if (!value) {
    return undefined;
  }

  return new Date(`${value}T12:00:00`).getTime();
};

export const utcMilliToDateInput = (value?: number | null) => {
  if (!value) {
    return "";
  }

  return new Date(value).toISOString().slice(0, 10);
};

export const formatBillingAddress = (
  billingSnapshot?: CmpInvoiceDetailFieldsFragment["billingSnapshot"] | null
) =>
  [
    billingSnapshot?.addressLine1,
    billingSnapshot?.addressLine2,
    billingSnapshot?.city,
    billingSnapshot?.province,
    billingSnapshot?.postalCode,
    billingSnapshot?.country,
  ]
    .filter(Boolean)
    .join(", ");

export const getInvoiceTypeFromKind = (
  invoiceKind?: CmpInvoiceKindEnum | null
): MockInvoiceType => {
  switch (invoiceKind) {
    case CmpInvoiceKindEnum.EventRegistration:
      return "eventTicket";
    case CmpInvoiceKindEnum.EventSponsorship:
      return "eventSponsorship";
    case CmpInvoiceKindEnum.Advertising:
    case CmpInvoiceKindEnum.Custom:
      // Advertising is sold as a standalone, custom-style invoice.
      return "custom";
    case CmpInvoiceKindEnum.Application:
    case CmpInvoiceKindEnum.Renewal:
    default:
      return "membership";
  }
};

export const getSourceKindForMockKind = (
  sourceKind?: CmpOperationalSourceKindEnum | null
): MockInvoiceSourceKind => {
  switch (sourceKind) {
    case CmpOperationalSourceKindEnum.EventRegistration:
      return "eventRegistration";
    case CmpOperationalSourceKindEnum.EventSponsorship:
      return "eventSponsorship";
    case CmpOperationalSourceKindEnum.Advertising:
    case CmpOperationalSourceKindEnum.CustomInvoice:
      return "standalone";
    case CmpOperationalSourceKindEnum.MemberApplication:
    case CmpOperationalSourceKindEnum.MemberRenewal:
    default:
      return "member";
  }
};

export const getInvoiceStatusFromDisplayState = (
  displayState: CmpInvoiceDisplayStateEnum
): MockInvoiceStatus => {
  switch (displayState) {
    case CmpInvoiceDisplayStateEnum.Draft:
      return "draft";
    case CmpInvoiceDisplayStateEnum.Paid:
      return "paid";
    case CmpInvoiceDisplayStateEnum.Waived:
      return "waived";
    case CmpInvoiceDisplayStateEnum.Voided:
      return "void";
    case CmpInvoiceDisplayStateEnum.Refunded:
      return "refunded";
    case CmpInvoiceDisplayStateEnum.PaymentFailed:
      return "failed";
    case CmpInvoiceDisplayStateEnum.PaymentAbandoned:
      return "abandoned";
    case CmpInvoiceDisplayStateEnum.PaymentExpired:
      return "expired";
    case CmpInvoiceDisplayStateEnum.ActionRequired:
      return "actionRequired";
    case CmpInvoiceDisplayStateEnum.NotPaid:
    default:
      return "unpaid";
  }
};

export const getInvoiceSourceLabel = ({
  invoiceKind,
  sourceKind,
  memberId,
  eventRegistrationId,
  eventSponsorshipRequestId,
}: {
  invoiceKind?: CmpInvoiceKindEnum | null;
  sourceKind?: CmpOperationalSourceKindEnum | null;
  memberId?: string | null;
  eventRegistrationId?: string | null;
  eventSponsorshipRequestId?: string | null;
}) => {
  if (sourceKind === CmpOperationalSourceKindEnum.CustomInvoice) {
    return "Custom invoice";
  }

  if (sourceKind === CmpOperationalSourceKindEnum.EventRegistration) {
    return eventRegistrationId
      ? `Event registration: ${eventRegistrationId}`
      : "Event registration";
  }

  if (sourceKind === CmpOperationalSourceKindEnum.EventSponsorship) {
    return eventSponsorshipRequestId
      ? `Event sponsorship: ${eventSponsorshipRequestId}`
      : "Event sponsorship";
  }

  if (
    invoiceKind === CmpInvoiceKindEnum.Application ||
    sourceKind === CmpOperationalSourceKindEnum.MemberApplication
  ) {
    return memberId ? `Member application: ${memberId}` : "Member application";
  }

  return memberId ? `Member: ${memberId}` : "Member invoice";
};

export const getRequestedPaymentMethodLabel = (
  paymentChoice?: CmpInvoicePaymentChoiceFieldsFragment | null
) => {
  if (!paymentChoice) {
    return "Invoice Me";
  }

  if (paymentChoice.type === CmpPaymentChoiceTypeEnum.CreditCard) {
    return "Credit card checkout";
  }

  if (paymentChoice.type === CmpPaymentChoiceTypeEnum.Free) {
    return "Waived / no payment required";
  }

  if (paymentChoice.type === CmpPaymentChoiceTypeEnum.ThirdParty) {
    return "Third-party payer";
  }

  return paymentChoice.manualPaymentMethodLabel || "Invoice Me";
};

export const getPaymentChoiceTypeForLabel = (label: string) => {
  if (label === "Credit card checkout") {
    return CmpPaymentChoiceTypeEnum.CreditCard;
  }

  if (label === "Waived / no payment required") {
    return CmpPaymentChoiceTypeEnum.Free;
  }

  if (label === "Third-party payer") {
    return CmpPaymentChoiceTypeEnum.ThirdParty;
  }

  return CmpPaymentChoiceTypeEnum.Manual;
};

export const toBackendLineItems = (lineItems: MockInvoiceLineItem[]) =>
  lineItems.map((item) => ({
    id: item.id,
    itemCode: item.code?.trim() || undefined,
    description: item.description,
    quantity: item.quantity,
    unitPriceInCents: item.unitPriceInCents,
    taxRatePercentage: item.taxRate,
  }));

export const toMockLineItems = (
  lineItems:
    | CmpInvoiceDetailFieldsFragment["lineItems"]
    | CmpInvoiceDetailFieldsFragment["invoice"]["lineItems"]
    | null
    | undefined
): MockInvoiceLineItem[] =>
  (lineItems || []).map((item) => ({
    id: item.id,
    description: item.description,
    quantity: item.quantity,
    unitPriceInCents: item.unitPriceInCents,
    taxRate: item.taxRatePercentage,
  }));

const getEventLabel = (eventType: CmpOperationalEventTypeEnum) =>
  String(eventType)
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const getHistoryFromDetail = (
  detail: CmpInvoiceDetailFieldsFragment
): MockInvoiceHistoryEvent[] => {
  const operationalEvents = detail.operationalEvents.map((event) => ({
    id: event.id,
    label: getEventLabel(event.eventType),
    occurredAt: utcMilliToDateInput(event.createdAtUtcMilli),
    actor: "CMP Staff",
    detail: event.summary,
  }));
  const emails = detail.outboundEmails.map((email) => ({
    id: email.id,
    label: `Email ${email.status.toLowerCase()}`,
    occurredAt: utcMilliToDateInput(
      email.sentAtUtcMilli || email.failedAtUtcMilli || email.createdAtUtcMilli
    ),
    actor: email.fromEmail,
    detail: `${email.subject}. Intended: ${
      email.intendedToEmail || email.toEmail
    }. Actual: ${email.toEmail}.`,
  }));
  const payments = detail.payments.map((payment) => ({
    id: payment.id,
    label: `Payment ${payment.status.toLowerCase()}`,
    occurredAt: utcMilliToDateInput(payment.receivedAtUtcMilli),
    actor: payment.paymentMethodLabel,
    detail: payment.note || payment.reference || "Payment record captured.",
  }));
  const refunds = detail.refunds.map((refund) => ({
    id: refund.id,
    label: `Refund ${refund.status.toLowerCase()}`,
    occurredAt: utcMilliToDateInput(
      refund.completedAtUtcMilli || refund.failedAtUtcMilli || refund.createdAtUtcMilli
    ),
    actor: refund.paymentMethodLabel || "CMP Staff",
    detail: refund.reason || "Refund record captured.",
  }));
  const history = [...operationalEvents, ...emails, ...payments, ...refunds].sort(
    (first, second) =>
      new Date(`${second.occurredAt}T00:00:00`).getTime() -
      new Date(`${first.occurredAt}T00:00:00`).getTime()
  );

  return history.length
    ? history
    : [
        {
          id: `${detail.invoice.id}-created`,
          label: "Invoice created",
          occurredAt: utcMilliToDateInput(detail.invoice.createdAtUtcMilli),
          actor: "CMP Staff",
          detail: `Invoice ${detail.invoice.invoiceNumber} exists in CAP backend records.`,
        },
      ];
};

export const invoiceFromManageRow = (
  row: CmpManageInvoiceRowFieldsFragment
): MockInvoice => {
  const recipientOverrideUsed = Boolean(
    row.actualEmailRecipient &&
      row.intendedEmailRecipient &&
      row.actualEmailRecipient.toLowerCase() !== row.intendedEmailRecipient.toLowerCase()
  );

  return {
    id: row.id,
    invoiceNumber: row.invoiceNumber,
    isCmpInvoiceModuleTestData: Boolean(row.isCmpInvoiceModuleTestData),
    type: getInvoiceTypeFromKind(row.invoiceKind),
    sourceKind: "standalone",
    sourceLabel: row.sourceLabel,
    customerName: row.customerName,
    billingContactName: row.customerName,
    billingEmail: row.intendedEmailRecipient || row.actualEmailRecipient || "",
    billingPhone: "",
    billingAddress: "",
    issueDate: utcMilliToDateInput(row.issuedAtUtcMilli),
    dueDate: utcMilliToDateInput(row.dueAtUtcMilli),
    daysPastDue: typeof row.daysPastDue === "number" ? row.daysPastDue : null,
    status: getInvoiceStatusFromDisplayState(row.displayState),
    paymentMethodLabel: row.paymentMethodLabel,
    requestedPaymentMethodLabel: "See invoice detail",
    lineItems: [
      {
        id: `${row.id}-summary-line`,
        description: row.sourceLabel,
        quantity: 1,
        unitPriceInCents: row.totalAmountInCents,
        taxRate: 0,
      },
    ],
    notes: "",
    publicMemo: "",
    emailRouting: {
      intendedRecipientEmail: row.intendedEmailRecipient || "No invoice email recorded",
      actualRecipientEmail: row.actualEmailRecipient || undefined,
      recipientOverrideUsed,
      routingMode: row.actualEmailRecipient
        ? recipientOverrideUsed
          ? "TEST_INBOX_OVERRIDE"
          : "LIVE_RECIPIENTS"
        : "NOT_SENT",
      state: row.actualEmailRecipient ? "sent" : "notSent",
    },
    history: [],
  };
};

export const invoiceFromDetail = (
  detail: CmpInvoiceDetailFieldsFragment
): MockInvoice => {
  const invoice = detail.invoice;
  const billingSnapshot = detail.billingSnapshot || invoice.billingSnapshot;
  const latestPaidPayment = detail.payments
    .filter((payment) => ["PAID", "RECORDED", "REFUNDED"].includes(payment.status))
    .sort((first, second) => second.receivedAtUtcMilli - first.receivedAtUtcMilli)[0];
  const latestEmail = [...detail.outboundEmails].sort(
    (first, second) =>
      (second.createdAtUtcMilli || 0) - (first.createdAtUtcMilli || 0)
  )[0];

  return {
    id: invoice.id,
    invoiceNumber: invoice.invoiceNumber,
    isCmpInvoiceModuleTestData: Boolean(invoice.isCmpInvoiceModuleTestData),
    type: getInvoiceTypeFromKind(invoice.invoiceKind),
    sourceKind: getSourceKindForMockKind(invoice.sourceKind),
    sourceLabel: getInvoiceSourceLabel({
      invoiceKind: invoice.invoiceKind,
      sourceKind: invoice.sourceKind,
      memberId: invoice.chamberMemberId,
      eventRegistrationId: invoice.eventRegistrationId,
      eventSponsorshipRequestId: invoice.eventSponsorshipRequestId,
    }),
    customerName: billingSnapshot?.customerName || "Invoice customer",
    billingContactName: billingSnapshot?.contactName || billingSnapshot?.customerName || "",
    billingEmail: billingSnapshot?.contactEmail || "",
    billingPhone: billingSnapshot?.contactPhone || "",
    billingAddress: formatBillingAddress(billingSnapshot),
    issueDate: utcMilliToDateInput(invoice.issuedAtUtcMilli),
    dueDate: utcMilliToDateInput(invoice.dueAtUtcMilli),
    daysPastDue: typeof detail.daysPastDue === "number" ? detail.daysPastDue : null,
    paidDate: utcMilliToDateInput(invoice.paidAtUtcMilli),
    status: getInvoiceStatusFromDisplayState(detail.displayState),
    paymentMethodLabel:
      latestPaidPayment?.paymentMethodLabel ||
      getPaymentMethodFallbackLabel(invoice.invoiceStatus),
    requestedPaymentMethodLabel: getRequestedPaymentMethodLabel(invoice.paymentChoice),
    lineItems: toMockLineItems(detail.lineItems.length ? detail.lineItems : invoice.lineItems),
    notes: invoice.internalNotes || "",
    publicMemo: invoice.publicMemo || "",
    emailRouting: {
      intendedRecipientEmail:
        latestEmail?.intendedToEmail || billingSnapshot?.contactEmail || "",
      actualRecipientEmail: latestEmail?.toEmail || undefined,
      recipientOverrideUsed: Boolean(latestEmail?.recipientOverrideUsed),
      routingMode: latestEmail
        ? latestEmail.recipientOverrideUsed
          ? "TEST_INBOX_OVERRIDE"
          : "LIVE_RECIPIENTS"
        : "NOT_SENT",
      lastSentAt: utcMilliToDateInput(latestEmail?.sentAtUtcMilli),
      state: latestEmail
        ? latestEmail.status === "SENT"
          ? "sent"
          : latestEmail.status === "FAILED"
            ? "failed"
            : "skipped"
        : "notSent",
    },
    history: getHistoryFromDetail(detail),
    replacementInvoiceId: invoice.replacementInvoiceId || undefined,
    replacesInvoiceId: invoice.replacesInvoiceId || undefined,
    providerMode:
      invoice.paymentChoice.type === CmpPaymentChoiceTypeEnum.CreditCard
        ? "Stripe test"
        : "Manual",
  };
};

// POL-017: Create Member's invoice preview renders its freshly created
// invoice records through the shared InvoicePaper, which consumes the Manage
// Invoices detail/row fragment shapes. CmpInvoiceRecordFieldsFragment is
// structurally identical to CmpInvoiceDetailFieldsFragment["invoice"] (both
// select CmpInvoiceRecordFields), so these builders wrap a record with honest
// defaults and empty collections.
export const invoiceDisplayStateFromStatus = (
  status: CmpInvoiceStatusEnum
): CmpInvoiceDisplayStateEnum => {
  switch (status) {
    case CmpInvoiceStatusEnum.Paid:
      return CmpInvoiceDisplayStateEnum.Paid;
    case CmpInvoiceStatusEnum.Unpaid:
      return CmpInvoiceDisplayStateEnum.NotPaid;
    case CmpInvoiceStatusEnum.Waived:
      return CmpInvoiceDisplayStateEnum.Waived;
    case CmpInvoiceStatusEnum.Void:
      return CmpInvoiceDisplayStateEnum.Voided;
    case CmpInvoiceStatusEnum.Refunded:
      return CmpInvoiceDisplayStateEnum.Refunded;
    // Honest draft mapping (Q-029 default): a just-created, not-yet-approved
    // invoice previews as a Draft document, same as Manage Invoices'
    // draft preview (INV-PREVIEW-002) — do not force NotPaid here.
    case CmpInvoiceStatusEnum.Draft:
    case CmpInvoiceStatusEnum.NotInvoiced:
    default:
      return CmpInvoiceDisplayStateEnum.Draft;
  }
};

export const invoiceDetailFromRecord = (
  invoice: CmpInvoiceRecordFieldsFragment
): CmpInvoiceDetailFieldsFragment => ({
  invoice,
  displayState: invoiceDisplayStateFromStatus(invoice.invoiceStatus),
  overdueBucket: CmpInvoiceOverdueBucketEnum.NotApplicable,
  daysPastDue: null,
  billingSnapshot: invoice.billingSnapshot,
  lineItems: invoice.lineItems ?? [],
  payments: [],
  refunds: [],
  outboundEmails: [],
  operationalEvents: [],
  availableActions: [],
  generatedAtUtcMilli: Date.now(),
});

export const manageInvoiceRowFromRecord = (
  invoice: CmpInvoiceRecordFieldsFragment
): CmpManageInvoiceRowFieldsFragment => ({
  id: invoice.id,
  invoiceNumber: invoice.invoiceNumber,
  customerName: invoice.billingSnapshot?.customerName || "",
  sourceLabel: "",
  invoiceKind: invoice.invoiceKind ?? CmpInvoiceKindEnum.Application,
  displayState: invoiceDisplayStateFromStatus(invoice.invoiceStatus),
  overdueBucket: CmpInvoiceOverdueBucketEnum.NotApplicable,
  daysPastDue: null,
  issuedAtUtcMilli: invoice.issuedAtUtcMilli,
  dueAtUtcMilli: invoice.dueAtUtcMilli,
  amountDueInCents: invoice.amountDueInCents,
  totalAmountInCents: invoice.totalAmountInCents ?? 0,
  totalPaidInCents: 0,
  balanceDueInCents: invoice.amountDueInCents,
  paymentMethodLabel: "-",
  paymentPlan: invoice.paymentPlan,
  // paymentRules is nullable on the record but required on the row. Module-10
  // invoices always carry rules; this neutral manual-friendly fallback only
  // guards the nullable field — an empty PAYMENT OPTIONS box downstream is a
  // backend-data signal, so do not fabricate chamber payment methods here.
  paymentRules: invoice.paymentRules ?? {
    ruleKind: CmpInvoicePaymentRuleKindEnum.Standard,
    summary: "",
    creditCardAllowed: false,
    manualPaymentAllowed: true,
    manualConfirmationRequired: false,
    allowedManualPaymentMethodIds: [],
    allowedManualPaymentMethodLabels: [],
  },
  actualEmailRecipient: null,
  intendedEmailRecipient: invoice.billingSnapshot?.contactEmail ?? null,
  isCmpInvoiceModuleTestData: invoice.isCmpInvoiceModuleTestData,
  availableActions: [],
});

export const applyLookupBillingSnapshot = (
  option: CmpInvoiceLookupOptionFieldsFragment
) => ({
  customerName: option.billingSnapshot.customerName,
  billingContactName:
    option.billingSnapshot.contactName || option.billingSnapshot.customerName,
  billingEmail: option.billingSnapshot.contactEmail,
  billingPhone: option.billingSnapshot.contactPhone || "",
  billingAddress: formatBillingAddress(option.billingSnapshot),
});
